'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { Heading } from '../../ui/heading';
import { ProductColors } from '../product-colors';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { AddToCartButton } from '../add-to-cart-button';
import { isSellable } from '@eliadmin/core/src/utils/product-utils';
import { useVariantsStore, VariantsProvider } from '@eliadmin/core/src/stores/variant-store';
import { useComparisonStore } from '../../../stores/comparison-store';
import { useEffect, useMemo } from 'react';
import { DialogProvider } from '../../ui/dialog';
import { ButtonIcon } from '../../ui/button-icon';
import { LuMinus } from 'react-icons/lu';

export type ComparisonVariantChange = (productId: number, variant?: VariantsEntity) => void;

type ComparisonProductProps = {
  product: Product;
  /** Remounts the variant state once the full detail replaces the stored card data. */
  isFull: boolean;
  onVariantChange: ComparisonVariantChange;
};

function getVariantLabel(variant: VariantsEntity): string {
  if (variant.name) return variant.name;
  if (variant.values?.length) {
    return variant.values.map(v => v.name ?? v.value).join(' / ');
  }
  return `Varianta ${variant.id}`;
}

function ComparisonProductInternal({
  product,
  onVariantChange,
}: Omit<ComparisonProductProps, 'isFull'>) {
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const setSelectedVariant = useVariantsStore(state => state.setSelectedVariant);
  const removeProduct = useComparisonStore(s => s.removeProduct);

  const variants = useMemo(() => product.variants ?? [], [product.variants]);
  const activeVariant = selectedVariant ?? variants[0];

  useEffect(() => {
    if (!selectedVariant && variants[0]) {
      setSelectedVariant(variants[0]);
    }
  }, [selectedVariant, variants, setSelectedVariant]);

  // The parameter rows live outside this provider, so they follow the selection through the parent.
  useEffect(() => {
    onVariantChange(product.id, selectedVariant ?? undefined);
  }, [onVariantChange, product.id, selectedVariant]);

  const handleVariantChange = (value: string) => {
    const variant = variants.find(v => String(v.id) === value);
    if (variant) setSelectedVariant(variant);
  };

  const variantImage = selectedVariant?.images?.[0];
  const image = variantImage?.url ? variantImage : product.image;
  const productUrl = ROUTES.PRODUCT({ slug: product.slug });

  return (
    <div className="flex h-full w-full flex-col items-start justify-start gap-4 sm:gap-6">
      <div className="relative w-full">
        <Link
          prefetch={false}
          href={productUrl}
          className="block aspect-square w-full rounded-[12px] bg-[#F9FAFB] p-[10px] sm:p-3"
        >
          <Image
            src={image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(image, product.name)}
            width={0}
            height={0}
            className="aspect-square w-full rounded-[12px] bg-white object-contain object-center p-2 lg:p-3"
            sizes="(max-width: 640px) 240px, (max-width: 1024px) 280px, 332px"
          />
        </Link>
        <button
          type="button"
          onClick={() => removeProduct(product.id)}
          aria-label={`Odstrániť z porovnania: ${product.name}`}
          className="absolute right-3 top-3 z-10 flex h-8 w-11 items-center justify-center rounded-full bg-[#FFF06F] transition-all ease-in-out hover:opacity-90 sm:h-12 sm:w-[68px]"
        >
          <LuMinus size={20} />
        </button>
      </div>

      <div className="flex w-full flex-col items-start justify-start gap-3 md:gap-4">
        <Link prefetch={false} href={productUrl} className="w-full">
          <Heading
            title={selectedVariant?.name ?? product.name}
            tag="h2"
            styleAs="h5"
            className={{
              title: 'line-clamp-2 transition-all ease-in-out hover:text-primary',
              container: 'w-full',
            }}
          />
        </Link>
        <ProductPrice product={product} />
        <ProductColors product={product} />
      </div>

      {variants.length > 0 && (
        <Select
          value={activeVariant ? String(activeVariant.id) : undefined}
          onValueChange={handleVariantChange}
        >
          <SelectTrigger
            expandIcon={
              <ButtonIcon
                alt={activeVariant?.name ?? ''}
                url="/images/icons/arrow-down-primary.svg"
              />
            }
            className={cn(
              'line-clamp-1 rounded-full border border-border bg-transparent',
              'ea-comparison-product__variant-select-trigger',
            )}
          >
            <SelectValue placeholder="Vyberte variantu" className="line-clamp-1" />
          </SelectTrigger>
          <SelectContent>
            {variants.map(variant => (
              <SelectItem key={variant.id} value={String(variant.id)}>
                {getVariantLabel(variant)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <AddToCartButton
        product={product}
        showIcon={true}
        sellable={isSellable(product, 1)}
        quantity={1}
        customText="Kúpiť"
        className={cn('mt-auto max-h-12 min-h-12 w-full', 'ea-product-card__add-to-cart-button')}
        fullSize
      />
    </div>
  );
}

export function ComparisonProduct({ product, isFull, onVariantChange }: ComparisonProductProps) {
  return (
    <VariantsProvider
      key={isFull ? 'full' : 'partial'}
      state={{ product, selectedQuantity: product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ComparisonProductInternal product={product} onVariantChange={onVariantChange} />
      </DialogProvider>
    </VariantsProvider>
  );
}
